import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import {
  LayoutDashboard, FileText, Receipt, CreditCard, FileX,
  ShoppingCart, Wallet, Upload, PiggyBank, Building2,
  Mail, TrendingUp, BarChart3, Calculator, Settings,
  Plus, Search, Package, Edit, Trash2, ArrowRight,
  DollarSign, Users, Briefcase, ChevronRight, Shield,
  Lock, KeyRound, QrCode, AlertTriangle, CheckCircle2, X,
  Eye, EyeOff, Smartphone
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ==================== 2FA VERIFICATION DIALOG ====================
const TwoFactorDialog = ({ open, onClose, onVerified }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleVerify = async () => {
    if (code.length < 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const res = await axios.post(`${API}/auth/verify-finance-access`, { code });
      localStorage.setItem('finance_token', res.data.finance_token);
      toast.success('Finance access verified!');
      onVerified();
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-600" />
            Security Verification Required
          </DialogTitle>
          <DialogDescription>
            Enter your 2FA code to access the Finance Workspace
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-3">
              <Lock className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-900">High Security Area</p>
                <p className="text-sm text-amber-700">
                  Finance Workspace contains sensitive financial data. 
                  Please verify your identity using your authenticator app.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Enter 6-digit code from your authenticator app</Label>
            <Input
              type="text"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              className="text-center text-2xl tracking-widest font-mono"
              autoFocus
            />
            {error && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertTriangle className="h-4 w-4" /> {error}
              </p>
            )}
          </div>

          <p className="text-xs text-muted-foreground text-center">
            Or enter a backup code if you don't have access to your authenticator
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleVerify} disabled={loading || code.length < 6}>
            {loading ? 'Verifying...' : 'Verify & Access'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== 2FA SETUP DIALOG ====================
const TwoFactorSetupDialog = ({ open, onClose }) => {
  const [step, setStep] = useState(1);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  const startSetup = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/2fa/setup`);
      setSetupData(res.data);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start 2FA setup');
    } finally {
      setLoading(false);
    }
  };

  const verifySetup = async () => {
    if (verifyCode.length < 6) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/2fa/verify-setup`, { code: verifyCode });
      toast.success('2FA enabled successfully!');
      setStep(3);
      setShowBackupCodes(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSetupData(null);
    setVerifyCode('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-green-600" />
            Setup Two-Factor Authentication
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 py-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Why enable 2FA?</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Adds an extra layer of security to your account</li>
                <li>• Required for accessing the Finance Workspace</li>
                <li>• Protects sensitive financial data</li>
              </ul>
            </div>
            
            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
              <Smartphone className="h-8 w-8 text-slate-600" />
              <div>
                <p className="font-medium">You'll need an authenticator app</p>
                <p className="text-sm text-muted-foreground">
                  Google Authenticator, Microsoft Authenticator, or Authy
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button onClick={startSetup} disabled={loading}>
                {loading ? 'Setting up...' : 'Begin Setup'}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && setupData && (
          <div className="space-y-4 py-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-4">
                Scan this QR code with your authenticator app
              </p>
              <div className="inline-block p-4 bg-white border rounded-lg">
                <img 
                  src={`data:image/png;base64,${setupData.qr_code}`} 
                  alt="2FA QR Code" 
                  className="w-48 h-48"
                />
              </div>
            </div>

            <div className="text-center">
              <p className="text-xs text-muted-foreground mb-2">Or enter this code manually:</p>
              <code className="bg-slate-100 px-3 py-1 rounded text-sm font-mono">
                {setupData.secret}
              </code>
            </div>

            <div className="space-y-2">
              <Label>Enter the 6-digit code from your app to verify</Label>
              <Input
                type="text"
                maxLength={6}
                placeholder="000000"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                className="text-center text-xl tracking-widest font-mono"
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={verifySetup} disabled={loading || verifyCode.length < 6}>
                {loading ? 'Verifying...' : 'Verify & Enable'}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 3 && setupData && (
          <div className="space-y-4 py-4">
            <div className="text-center">
              <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-green-700">2FA Enabled Successfully!</h3>
            </div>

            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <h4 className="font-medium text-amber-900 mb-2 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" /> Save your backup codes
              </h4>
              <p className="text-sm text-amber-700 mb-3">
                Store these codes safely. You can use them to access your account if you lose your phone.
              </p>
              
              <div className="grid grid-cols-4 gap-2">
                {setupData.backup_codes.map((code, idx) => (
                  <code key={idx} className="bg-white px-2 py-1 rounded text-xs font-mono text-center border">
                    {code}
                  </code>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button onClick={handleClose}>Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ==================== ACCESS DENIED COMPONENT ====================
const AccessDenied = ({ onSetup2FA }) => {
  const { user } = useAuth();
  const hasPermission = user?.permissions?.can_access_finance_workspace;
  
  return (
    <div className="p-6 max-w-2xl mx-auto">
      <Card className="border-red-200 bg-red-50">
        <CardContent className="p-8 text-center">
          <Shield className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-red-900 mb-2">Access Restricted</h2>
          
          {!hasPermission ? (
            <>
              <p className="text-red-700 mb-6">
                You don't have permission to access the Finance Workspace. 
                Please contact your administrator to request access.
              </p>
              <Badge variant="destructive" className="text-sm">
                Missing Permission: can_access_finance_workspace
              </Badge>
            </>
          ) : (
            <>
              <p className="text-red-700 mb-6">
                Two-Factor Authentication is required to access the Finance Workspace.
                Please set up 2FA to continue.
              </p>
              <Button onClick={onSetup2FA} size="lg" className="gap-2">
                <KeyRound className="h-5 w-5" /> Setup 2FA Now
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== CUSTOMER RECEIPTS COMPONENT ====================
const CustomerReceiptsManager = ({ clients, invoices, onClose }) => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    client_id: '', client_name: '', invoice_id: '', amount: 0,
    payment_method: 'eft', reference: '', bank_account: '',
    payment_date: new Date().toISOString().split('T')[0], notes: ''
  });

  useEffect(() => { loadReceipts(); }, []);

  const loadReceipts = async () => {
    try {
      const res = await axios.get(`${API}/customer-receipts`);
      setReceipts(res.data);
    } catch (error) {
      toast.error('Failed to load receipts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.client_name || form.amount <= 0) {
      toast.error('Please fill in required fields');
      return;
    }
    try {
      await axios.post(`${API}/customer-receipts`, form);
      toast.success('Receipt created successfully');
      loadReceipts();
      setShowForm(false);
      setForm({
        client_id: '', client_name: '', invoice_id: '', amount: 0,
        payment_method: 'eft', reference: '', bank_account: '',
        payment_date: new Date().toISOString().split('T')[0], notes: ''
      });
    } catch (error) {
      toast.error('Failed to create receipt');
    }
  };

  const selectClient = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setForm({ ...form, client_id: clientId, client_name: client.company || client.name });
    }
  };

  const selectInvoice = (invoiceId) => {
    const invoice = invoices.find(i => i.id === invoiceId);
    if (invoice) {
      setForm({ 
        ...form, 
        invoice_id: invoiceId, 
        amount: invoice.balance || invoice.total,
        client_name: invoice.client_name || form.client_name
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Customer Receipts</h2>
          <p className="text-sm text-muted-foreground">Record payments received from customers</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>Back to Workspace</Button>
          <Button onClick={() => setShowForm(true)}>
            <Plus className="h-4 w-4 mr-2" /> Record Receipt
          </Button>
        </div>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Receipt #</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Invoice</TableHead>
              <TableHead>Method</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {receipts.map((receipt) => (
              <TableRow key={receipt.id}>
                <TableCell className="font-mono text-xs">{receipt.receipt_number}</TableCell>
                <TableCell>{new Date(receipt.payment_date || receipt.created_at).toLocaleDateString()}</TableCell>
                <TableCell>{receipt.client_name}</TableCell>
                <TableCell className="font-mono text-xs">{receipt.invoice_id?.slice(0,8) || '-'}</TableCell>
                <TableCell className="capitalize">{receipt.payment_method}</TableCell>
                <TableCell className="text-right font-mono font-medium text-green-600">
                  R {receipt.amount.toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
            {receipts.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No receipts recorded yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Customer Receipt</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Client</Label>
              <Select value={form.client_id || 'none'} onValueChange={selectClient}>
                <SelectTrigger><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select client...</SelectItem>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Link to Invoice (Optional)</Label>
              <Select value={form.invoice_id || 'none'} onValueChange={selectInvoice}>
                <SelectTrigger><SelectValue placeholder="Select invoice" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No invoice</SelectItem>
                  {invoices.filter(i => i.status !== 'paid').map(i => (
                    <SelectItem key={i.id} value={i.id}>
                      {i.invoice_number} - R {i.balance?.toLocaleString() || i.total?.toLocaleString()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount Received *</Label>
              <Input 
                type="number" 
                value={form.amount} 
                onChange={(e) => setForm({...form, amount: parseFloat(e.target.value) || 0})}
              />
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({...form, payment_method: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="eft">EFT / Bank Transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Payment Date</Label>
              <Input 
                type="date" 
                value={form.payment_date} 
                onChange={(e) => setForm({...form, payment_date: e.target.value})}
              />
            </div>
            <div>
              <Label>Reference</Label>
              <Input 
                value={form.reference} 
                onChange={(e) => setForm({...form, reference: e.target.value})}
                placeholder="Bank reference"
              />
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea 
                value={form.notes} 
                onChange={(e) => setForm({...form, notes: e.target.value})}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={handleSubmit}>Record Receipt</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== CREDIT NOTES COMPONENT ====================
const CreditNotesManager = ({ clients, invoices, onClose }) => {
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    invoice_id: '', client_id: '', client_name: '', client_email: '',
    reason: 'goods_returned', items: [], subtotal: 0, vat_rate: 15,
    vat_amount: 0, total: 0, notes: ''
  });

  useEffect(() => { loadCreditNotes(); }, []);

  const loadCreditNotes = async () => {
    try {
      const res = await axios.get(`${API}/credit-notes`);
      setCreditNotes(res.data);
    } catch (error) {
      toast.error('Failed to load credit notes');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = (subtotal, vatRate) => {
    const vatAmount = subtotal * (vatRate / 100);
    return { subtotal, vat_amount: vatAmount, total: subtotal + vatAmount };
  };

  const handleSubmit = async () => {
    if (!form.client_name || form.total <= 0) {
      toast.error('Please fill in required fields');
      return;
    }
    try {
      await axios.post(`${API}/credit-notes`, form);
      toast.success('Credit note created successfully');
      loadCreditNotes();
      setShowForm(false);
    } catch (error) {
      toast.error('Failed to create credit note');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Credit Notes</h2>
          <p className="text-sm text-muted-foreground">Issue credits for returns or adjustments</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>Back to Workspace</Button>
          <Button onClick={() => setShowForm(true)}>
            <Plus className="h-4 w-4 mr-2" /> Create Credit Note
          </Button>
        </div>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Credit Note #</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {creditNotes.map((note) => (
              <TableRow key={note.id}>
                <TableCell className="font-mono text-xs">{note.credit_note_number}</TableCell>
                <TableCell>{new Date(note.created_at).toLocaleDateString()}</TableCell>
                <TableCell>{note.client_name}</TableCell>
                <TableCell className="capitalize">{note.reason?.replace('_', ' ')}</TableCell>
                <TableCell>
                  <Badge variant={note.status === 'applied' ? 'success' : 'secondary'}>
                    {note.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right font-mono font-medium text-red-600">
                  -R {note.total.toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
            {creditNotes.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No credit notes yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Credit Note</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Client Name *</Label>
              <Input 
                value={form.client_name} 
                onChange={(e) => setForm({...form, client_name: e.target.value})}
              />
            </div>
            <div>
              <Label>Reason</Label>
              <Select value={form.reason} onValueChange={(v) => setForm({...form, reason: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="goods_returned">Goods Returned</SelectItem>
                  <SelectItem value="overcharge">Overcharge</SelectItem>
                  <SelectItem value="service_issue">Service Issue</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount (excl. VAT) *</Label>
              <Input 
                type="number" 
                value={form.subtotal} 
                onChange={(e) => {
                  const subtotal = parseFloat(e.target.value) || 0;
                  const totals = calculateTotals(subtotal, form.vat_rate);
                  setForm({...form, ...totals});
                }}
              />
            </div>
            <div>
              <Label>VAT Rate %</Label>
              <Select value={String(form.vat_rate)} onValueChange={(v) => {
                const vatRate = parseFloat(v);
                const totals = calculateTotals(form.subtotal, vatRate);
                setForm({...form, vat_rate: vatRate, ...totals});
              }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0% (Exempt)</SelectItem>
                  <SelectItem value="15">15%</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 p-3 bg-slate-50 rounded-lg">
              <div className="flex justify-between text-sm">
                <span>Subtotal:</span>
                <span className="font-mono">R {form.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>VAT ({form.vat_rate}%):</span>
                <span className="font-mono">R {form.vat_amount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-bold mt-1 pt-1 border-t">
                <span>Total Credit:</span>
                <span className="font-mono text-red-600">R {form.total.toFixed(2)}</span>
              </div>
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea 
                value={form.notes} 
                onChange={(e) => setForm({...form, notes: e.target.value})}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={handleSubmit}>Create Credit Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== EXPENSES MANAGER COMPONENT ====================
const ExpensesManager = ({ suppliers, onClose }) => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    description: '', amount: 0, expense_date: new Date().toISOString().split('T')[0],
    category: 'general', supplier_name: '', payment_method: 'eft',
    vat_rate: 15, vat_amount: 0, receipt_reference: '', notes: ''
  });

  const categories = ['general', 'travel', 'office', 'utilities', 'supplies', 'marketing', 'other'];

  useEffect(() => { loadExpenses(); }, []);

  const loadExpenses = async () => {
    try {
      const res = await axios.get(`${API}/expenses`);
      setExpenses(res.data);
    } catch (error) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.description || form.amount <= 0) {
      toast.error('Please fill in required fields');
      return;
    }
    try {
      await axios.post(`${API}/expenses`, {
        ...form,
        vat_amount: form.amount * (form.vat_rate / 100)
      });
      toast.success('Expense recorded successfully');
      loadExpenses();
      setShowForm(false);
    } catch (error) {
      toast.error('Failed to record expense');
    }
  };

  const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Expenses</h2>
          <p className="text-sm text-muted-foreground">Track and manage business expenses</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>Back to Workspace</Button>
          <Button onClick={() => setShowForm(true)}>
            <Plus className="h-4 w-4 mr-2" /> Record Expense
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <p className="text-xs text-red-600">Total Expenses</p>
            <p className="text-2xl font-bold text-red-700">R {totalExpenses.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">This Month</p>
            <p className="text-2xl font-bold">
              R {expenses.filter(e => new Date(e.expense_date).getMonth() === new Date().getMonth())
                .reduce((sum, e) => sum + e.amount, 0).toLocaleString()}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Total Records</p>
            <p className="text-2xl font-bold">{expenses.length}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {expenses.map((expense) => (
              <TableRow key={expense.id}>
                <TableCell>{new Date(expense.expense_date).toLocaleDateString()}</TableCell>
                <TableCell className="font-medium">{expense.description}</TableCell>
                <TableCell><Badge variant="outline" className="capitalize">{expense.category}</Badge></TableCell>
                <TableCell>{expense.supplier_name || '-'}</TableCell>
                <TableCell className="text-right font-mono text-red-600">
                  R {expense.amount.toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
            {expenses.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  No expenses recorded yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Expense</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Description *</Label>
              <Input 
                value={form.description} 
                onChange={(e) => setForm({...form, description: e.target.value})}
                placeholder="What was the expense for?"
              />
            </div>
            <div>
              <Label>Amount *</Label>
              <Input 
                type="number" 
                value={form.amount} 
                onChange={(e) => setForm({...form, amount: parseFloat(e.target.value) || 0})}
              />
            </div>
            <div>
              <Label>Date</Label>
              <Input 
                type="date" 
                value={form.expense_date} 
                onChange={(e) => setForm({...form, expense_date: e.target.value})}
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({...form, payment_method: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="eft">EFT</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Supplier (Optional)</Label>
              <Select value={form.supplier_id || 'none'} onValueChange={(v) => {
                const supplier = suppliers.find(s => s.id === v);
                setForm({...form, supplier_id: v === 'none' ? '' : v, supplier_name: supplier?.company_name || ''});
              }}>
                <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No supplier</SelectItem>
                  {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Receipt Reference</Label>
              <Input 
                value={form.receipt_reference} 
                onChange={(e) => setForm({...form, receipt_reference: e.target.value})}
              />
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea 
                value={form.notes} 
                onChange={(e) => setForm({...form, notes: e.target.value})}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={handleSubmit}>Record Expense</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== SUPPLIER INVOICES COMPONENT ====================
const SupplierInvoicesManager = ({ suppliers, onClose }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    supplier_name: '', supplier_email: '', invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '', subtotal: 0, vat_rate: 15, vat_amount: 0, total: 0, notes: ''
  });

  useEffect(() => { loadInvoices(); }, []);

  const loadInvoices = async () => {
    try {
      const res = await axios.get(`${API}/supplier-invoices`);
      setInvoices(res.data);
    } catch (error) {
      toast.error('Failed to load supplier invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.supplier_name || !form.invoice_number || form.total <= 0) {
      toast.error('Please fill in required fields');
      return;
    }
    try {
      await axios.post(`${API}/supplier-invoices`, form);
      toast.success('Supplier invoice created');
      loadInvoices();
      setShowForm(false);
    } catch (error) {
      toast.error('Failed to create supplier invoice');
    }
  };

  const calculateTotals = (subtotal, vatRate) => {
    const vatAmount = subtotal * (vatRate / 100);
    return { subtotal, vat_amount: vatAmount, total: subtotal + vatAmount };
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Supplier Invoices</h2>
          <p className="text-sm text-muted-foreground">Bills from your suppliers</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>Back to Workspace</Button>
          <Button onClick={() => setShowForm(true)}>
            <Plus className="h-4 w-4 mr-2" /> Add Supplier Invoice
          </Button>
        </div>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Internal #</TableHead>
              <TableHead>Supplier Inv #</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Balance</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((inv) => (
              <TableRow key={inv.id}>
                <TableCell className="font-mono text-xs">{inv.internal_number}</TableCell>
                <TableCell>{inv.invoice_number}</TableCell>
                <TableCell>{inv.supplier_name}</TableCell>
                <TableCell>{new Date(inv.invoice_date).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Badge variant={inv.status === 'paid' ? 'success' : inv.status === 'partial' ? 'warning' : 'secondary'}>
                    {inv.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right font-mono">R {inv.total.toLocaleString()}</TableCell>
                <TableCell className="text-right font-mono text-red-600">R {inv.balance.toLocaleString()}</TableCell>
              </TableRow>
            ))}
            {invoices.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No supplier invoices yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Supplier Invoice</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Supplier *</Label>
              <Select onValueChange={(v) => {
                const supplier = suppliers.find(s => s.id === v);
                if (supplier) {
                  setForm({...form, supplier_id: v, supplier_name: supplier.company_name, supplier_email: supplier.email || ''});
                }
              }}>
                <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                <SelectContent>
                  {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Supplier Invoice Number *</Label>
              <Input 
                value={form.invoice_number} 
                onChange={(e) => setForm({...form, invoice_number: e.target.value})}
                placeholder="INV-001"
              />
            </div>
            <div>
              <Label>Invoice Date</Label>
              <Input 
                type="date" 
                value={form.invoice_date} 
                onChange={(e) => setForm({...form, invoice_date: e.target.value})}
              />
            </div>
            <div>
              <Label>Due Date</Label>
              <Input 
                type="date" 
                value={form.due_date} 
                onChange={(e) => setForm({...form, due_date: e.target.value})}
              />
            </div>
            <div>
              <Label>Amount (excl. VAT) *</Label>
              <Input 
                type="number" 
                value={form.subtotal} 
                onChange={(e) => {
                  const subtotal = parseFloat(e.target.value) || 0;
                  const totals = calculateTotals(subtotal, form.vat_rate);
                  setForm({...form, ...totals});
                }}
              />
            </div>
            <div>
              <Label>VAT Rate</Label>
              <Select value={String(form.vat_rate)} onValueChange={(v) => {
                const vatRate = parseFloat(v);
                const totals = calculateTotals(form.subtotal, vatRate);
                setForm({...form, vat_rate: vatRate, ...totals});
              }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0%</SelectItem>
                  <SelectItem value="15">15%</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 p-3 bg-slate-50 rounded-lg">
              <div className="flex justify-between font-bold">
                <span>Total:</span>
                <span className="font-mono">R {form.total.toFixed(2)}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={handleSubmit}>Add Invoice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== SUPPLIER PAYMENTS COMPONENT ====================
const SupplierPaymentsManager = ({ suppliers, supplierInvoices, onClose }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    supplier_name: '', supplier_invoice_id: '', amount: 0,
    payment_method: 'eft', reference: '',
    payment_date: new Date().toISOString().split('T')[0], notes: ''
  });

  useEffect(() => { loadPayments(); }, []);

  const loadPayments = async () => {
    try {
      const res = await axios.get(`${API}/supplier-payments`);
      setPayments(res.data);
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.supplier_name || form.amount <= 0) {
      toast.error('Please fill in required fields');
      return;
    }
    try {
      await axios.post(`${API}/supplier-payments`, form);
      toast.success('Payment recorded');
      loadPayments();
      setShowForm(false);
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Supplier Payments</h2>
          <p className="text-sm text-muted-foreground">Track payments to suppliers</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>Back to Workspace</Button>
          <Button onClick={() => setShowForm(true)}>
            <Plus className="h-4 w-4 mr-2" /> Record Payment
          </Button>
        </div>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Payment #</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.map((payment) => (
              <TableRow key={payment.id}>
                <TableCell className="font-mono text-xs">{payment.payment_number}</TableCell>
                <TableCell>{new Date(payment.payment_date || payment.created_at).toLocaleDateString()}</TableCell>
                <TableCell>{payment.supplier_name}</TableCell>
                <TableCell className="capitalize">{payment.payment_method}</TableCell>
                <TableCell>{payment.reference || '-'}</TableCell>
                <TableCell className="text-right font-mono text-red-600">
                  R {payment.amount.toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
            {payments.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No payments recorded yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Supplier Payment</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Supplier *</Label>
              <Select onValueChange={(v) => {
                const supplier = suppliers.find(s => s.id === v);
                if (supplier) {
                  setForm({...form, supplier_id: v, supplier_name: supplier.company_name});
                }
              }}>
                <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                <SelectContent>
                  {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Link to Invoice (Optional)</Label>
              <Select value={form.supplier_invoice_id || 'none'} onValueChange={(v) => {
                const inv = supplierInvoices.find(i => i.id === v);
                setForm({...form, supplier_invoice_id: v === 'none' ? '' : v, amount: inv?.balance || form.amount});
              }}>
                <SelectTrigger><SelectValue placeholder="Select invoice" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No invoice</SelectItem>
                  {supplierInvoices.filter(i => i.status !== 'paid').map(i => (
                    <SelectItem key={i.id} value={i.id}>
                      {i.invoice_number} - R {i.balance?.toLocaleString()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount *</Label>
              <Input 
                type="number" 
                value={form.amount} 
                onChange={(e) => setForm({...form, amount: parseFloat(e.target.value) || 0})}
              />
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({...form, payment_method: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="eft">EFT</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Payment Date</Label>
              <Input 
                type="date" 
                value={form.payment_date} 
                onChange={(e) => setForm({...form, payment_date: e.target.value})}
              />
            </div>
            <div>
              <Label>Reference</Label>
              <Input 
                value={form.reference} 
                onChange={(e) => setForm({...form, reference: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={handleSubmit}>Record Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== WORKSPACE TILE COMPONENT ====================
const WorkspaceTile = ({ icon: Icon, title, description, onClick, color = "blue", badge }) => (
  <Card 
    className={`cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02] border-l-4`}
    style={{ borderLeftColor: `var(--${color}-500, #3b82f6)` }}
    onClick={onClick}
    data-testid={`tile-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <CardContent className="p-4">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
          <Icon className="h-5 w-5 text-slate-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-sm truncate">{title}</h3>
            {badge && <Badge variant="secondary" className="text-xs">{badge}</Badge>}
          </div>
          <p className="text-xs text-muted-foreground line-clamp-2">{description}</p>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      </div>
    </CardContent>
  </Card>
);

// ==================== MAIN ACCOUNTING WORKSPACE COMPONENT ====================
export const AccountingWorkspace = () => {
  const navigate = useNavigate();
  const { user, canAccessFinance, hasPermission } = useAuth();
  
  // Security state
  const [isVerified, setIsVerified] = useState(false);
  const [checking, setChecking] = useState(true);
  const [show2FADialog, setShow2FADialog] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFAStatus, setTwoFAStatus] = useState({ enabled: false, required: false });
  const [isPrivilegedRole, setIsPrivilegedRole] = useState(false);
  
  // Workspace state
  const [activeView, setActiveView] = useState('workspace');
  const [stats, setStats] = useState({ totalRevenue: 0, totalExpenses: 0, pendingInvoices: 0, overdueInvoices: 0 });
  
  // Data for sub-components
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierInvoices, setSupplierInvoices] = useState([]);

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const financeToken = localStorage.getItem('finance_token');
      const res = await axios.get(`${API}/auth/check-finance-access`, {
        params: { finance_token: financeToken }
      });
      
      setTwoFAStatus({
        enabled: res.data.two_fa_enabled,
        required: res.data.two_fa_required
      });
      
      // Check if user has privileged role (super_admin or admin)
      const privileged = res.data.is_privileged_role || false;
      setIsPrivilegedRole(privileged);
      
      if (!res.data.has_permission) {
        setIsVerified(false);
      } else if (res.data.finance_access_valid || privileged) {
        // Privileged roles get auto-verified without 2FA
        setIsVerified(true);
        loadData();
      } else if (res.data.needs_verification) {
        if (res.data.two_fa_enabled) {
          setShow2FADialog(true);
        } else {
          // Need to setup 2FA first
          setShow2FASetup(true);
        }
      } else {
        // No 2FA required
        setIsVerified(true);
        loadData();
      }
    } catch (error) {
      console.error('Access check failed:', error);
    } finally {
      setChecking(false);
    }
  };

  const loadData = async () => {
    try {
      const [financeRes, clientsRes, invoicesRes, suppliersRes, suppInvRes] = await Promise.all([
        axios.get(`${API}/dashboard/finance`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/invoices`),
        axios.get(`${API}/suppliers`),
        axios.get(`${API}/supplier-invoices`)
      ]);
      
      setStats({
        totalRevenue: financeRes.data.total_revenue || 0,
        totalExpenses: financeRes.data.total_expenses || 0,
        pendingInvoices: financeRes.data.unpaid_invoices || 0,
        overdueInvoices: financeRes.data.overdue_invoices || 0
      });
      setClients(clientsRes.data);
      setInvoices(invoicesRes.data);
      setSuppliers(suppliersRes.data);
      setSupplierInvoices(suppInvRes.data);
    } catch (error) {
      console.error('Failed to load data');
    }
  };

  const handleVerified = () => {
    setShow2FADialog(false);
    setIsVerified(true);
    loadData();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount);
  };

  // Loading state
  if (checking) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Shield className="h-12 w-12 text-blue-600 mx-auto mb-4 animate-pulse" />
          <p className="text-muted-foreground">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Access denied
  if (!isVerified && !show2FADialog && !show2FASetup) {
    return <AccessDenied onSetup2FA={() => setShow2FASetup(true)} />;
  }

  // Render sub-views
  if (activeView === 'receipts') {
    return (
      <div className="p-6">
        <CustomerReceiptsManager clients={clients} invoices={invoices} onClose={() => setActiveView('workspace')} />
      </div>
    );
  }

  if (activeView === 'credit-notes') {
    return (
      <div className="p-6">
        <CreditNotesManager clients={clients} invoices={invoices} onClose={() => setActiveView('workspace')} />
      </div>
    );
  }

  if (activeView === 'expenses') {
    return (
      <div className="p-6">
        <ExpensesManager suppliers={suppliers} onClose={() => setActiveView('workspace')} />
      </div>
    );
  }

  if (activeView === 'supplier-invoices') {
    return (
      <div className="p-6">
        <SupplierInvoicesManager suppliers={suppliers} onClose={() => setActiveView('workspace')} />
      </div>
    );
  }

  if (activeView === 'supplier-payments') {
    return (
      <div className="p-6">
        <SupplierPaymentsManager suppliers={suppliers} supplierInvoices={supplierInvoices} onClose={() => setActiveView('workspace')} />
      </div>
    );
  }

  // Main workspace tiles
  const workspaceTiles = {
    sales: [
      { icon: LayoutDashboard, title: 'Dashboard', description: 'Overview of how your business is performing', action: () => navigate('/dashboard'), color: 'blue' },
      { icon: FileText, title: 'Create a Quote', description: 'Send a quotation to your customer', action: () => navigate('/sales?tab=quotations'), color: 'emerald' },
      { icon: Receipt, title: 'Create an Invoice', description: 'Sell items or services to your customer', action: () => navigate('/finance?tab=invoices'), color: 'amber' },
      { icon: CreditCard, title: 'Customer Receipts', description: 'Receive money from your customer', action: () => setActiveView('receipts'), color: 'green' },
      { icon: FileX, title: 'Create a Credit Note', description: 'Credit your customer for goods returned', action: () => setActiveView('credit-notes'), color: 'red' },
    ],
    purchases: [
      { icon: ShoppingCart, title: 'Create Supplier Invoice', description: 'Purchase items from a supplier', action: () => setActiveView('supplier-invoices'), color: 'purple' },
      { icon: Wallet, title: 'Supplier Payments', description: 'Pay your supplier', action: () => setActiveView('supplier-payments'), color: 'indigo' },
      { icon: Upload, title: 'Import Bank Statements', description: 'Import and view bank statement transactions', action: () => toast.info('Bank import coming soon'), color: 'cyan' },
      { icon: PiggyBank, title: 'Record Expenses', description: 'Manually capture expenses into your bank', action: () => setActiveView('expenses'), color: 'orange' },
      { icon: Building2, title: 'View Bank Transactions', description: 'View payments, receipts and bank transfers', action: () => toast.info('Bank transactions coming soon'), color: 'slate' },
    ],
    reports: [
      { icon: Mail, title: 'Send Customer Statements', description: 'Email or print statements to all your customers', action: () => navigate('/clients'), color: 'blue' },
      { icon: TrendingUp, title: 'Profit and Loss', description: 'View your Profit and Loss report', action: () => navigate('/finance?tab=reports'), color: 'emerald' },
      { icon: BarChart3, title: 'Balance Sheet', description: 'Balance sheet report', action: () => navigate('/finance?tab=reports'), color: 'violet' },
      { icon: Calculator, title: 'Prepare VAT Return', description: 'VAT returns and VAT reporting', action: () => toast.info('VAT returns coming soon'), color: 'amber' },
      { icon: Settings, title: 'Company Settings', description: 'Manage branding, financial years, VAT', action: () => navigate('/settings'), color: 'slate' },
    ]
  };

  return (
    <div className="p-6 space-y-6" data-testid="accounting-workspace">
      {/* 2FA Dialogs */}
      <TwoFactorDialog 
        open={show2FADialog} 
        onClose={() => setShow2FADialog(false)} 
        onVerified={handleVerified}
      />
      <TwoFactorSetupDialog 
        open={show2FASetup} 
        onClose={() => {
          setShow2FASetup(false);
          checkAccess();
        }} 
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-green-600" />
            Finance Workspace
          </h1>
          <p className="text-muted-foreground">Secure accounting management</p>
        </div>
        <div className="flex items-center gap-2">
          {isPrivilegedRole ? (
            <Badge variant="outline" className="gap-1 bg-purple-50 text-purple-700 border-purple-200">
              <Shield className="h-3 w-3" /> Admin Access
            </Badge>
          ) : (
            <Badge variant="outline" className="gap-1 bg-green-50 text-green-700 border-green-200">
              <Lock className="h-3 w-3" /> 2FA Protected
            </Badge>
          )}
          {!twoFAStatus.enabled && !isPrivilegedRole && (
            <Button variant="outline" size="sm" onClick={() => setShow2FASetup(true)}>
              <KeyRound className="h-4 w-4 mr-2" /> Setup 2FA
            </Button>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <DollarSign className="h-8 w-8 text-emerald-600" />
              <div>
                <p className="text-xs text-emerald-600">Total Revenue</p>
                <p className="text-xl font-bold font-mono text-emerald-700">{formatCurrency(stats.totalRevenue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-rose-50 border-rose-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Briefcase className="h-8 w-8 text-rose-600" />
              <div>
                <p className="text-xs text-rose-600">Total Expenses</p>
                <p className="text-xl font-bold font-mono text-rose-700">{formatCurrency(stats.totalExpenses)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 text-amber-600" />
              <div>
                <p className="text-xs text-amber-600">Pending Invoices</p>
                <p className="text-xl font-bold font-mono text-amber-700">{stats.pendingInvoices}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Receipt className="h-8 w-8 text-red-600" />
              <div>
                <p className="text-xs text-red-600">Overdue</p>
                <p className="text-xl font-bold font-mono text-red-700">{stats.overdueInvoices}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* My Workspace Section */}
      <Card>
        <CardHeader>
          <CardTitle>My Workspace</CardTitle>
          <CardDescription>Quick actions for your daily financial tasks</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Sales & Customers */}
          <div>
            <h3 className="font-semibold text-sm text-muted-foreground mb-3 flex items-center gap-2">
              <DollarSign className="h-4 w-4" /> Sales & Customers
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {workspaceTiles.sales.map((tile, idx) => (
                <WorkspaceTile key={idx} {...tile} onClick={tile.action} />
              ))}
            </div>
          </div>

          {/* Purchases & Suppliers */}
          <div>
            <h3 className="font-semibold text-sm text-muted-foreground mb-3 flex items-center gap-2">
              <ShoppingCart className="h-4 w-4" /> Purchases & Suppliers
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {workspaceTiles.purchases.map((tile, idx) => (
                <WorkspaceTile key={idx} {...tile} onClick={tile.action} />
              ))}
            </div>
          </div>

          {/* Reports & Settings */}
          <div>
            <h3 className="font-semibold text-sm text-muted-foreground mb-3 flex items-center gap-2">
              <BarChart3 className="h-4 w-4" /> Reports & Settings
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {workspaceTiles.reports.map((tile, idx) => (
                <WorkspaceTile key={idx} {...tile} onClick={tile.action} />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountingWorkspace;
